import { DateTime } from "luxon";

/** An instant as the API and the templates hand it around. */
export type VpInstant = string | number | Date;

/** One calendar day's worth of items, as `groupByDay` returns them. */
export interface VpDayGroup<T> {
  /** Local midnight that opens the day, in the grouping zone. */
  dayStart: Date;
  /** Marker text — "Today · Fri, Sep 4", "Yesterday · Thu, Sep 3", "Sun, Aug 30". */
  label: string;
  /** The items that fall on this day, in the order they were given. */
  items: T[];
}

// The day marker is English product copy ("Today", "Yesterday"), so the date
// half is pinned to the same locale rather than following the browser.
const LABEL_LOCALE = "en-US";
const DAY_FORMAT = "ccc, LLL d";

function toDateTime(instant: VpInstant, zone: string): DateTime {
  const dt =
    instant instanceof Date
      ? DateTime.fromJSDate(instant)
      : typeof instant === "number"
        ? DateTime.fromMillis(instant)
        : DateTime.fromISO(instant, { zone });
  return dt.setZone(zone).setLocale(LABEL_LOCALE);
}

/**
 * Day-marker label for the day that starts at `dayStart`, relative to `now`:
 * "Today · Fri, Sep 4", "Yesterday · Thu, Sep 3", otherwise "Sun, Aug 30".
 * `zone` is an IANA name; it defaults to the browser's zone.
 */
export function dayLabel(dayStart: VpInstant, now: VpInstant, zone?: string): string {
  const z = zone ?? "local";
  const day = toDateTime(dayStart, z).startOf("day");
  const today = toDateTime(now, z).startOf("day");
  const date = day.toFormat(DAY_FORMAT);
  const daysAgo = Math.round(today.diff(day, "days").days);
  if (daysAgo === 0) {
    return `Today · ${date}`;
  }
  if (daysAgo === 1) {
    return `Yesterday · ${date}`;
  }
  return date;
}

/**
 * Groups `items` by the calendar day their instant falls on in `zone`
 * (IANA name; defaults to the browser's), newest day first, items within a
 * day in the order given. Day boundaries are zone-aware, so the same instant
 * lands on different days for viewers in different zones. Items whose instant
 * cannot be parsed are left out. Empty input → `[]`.
 */
export function groupByDay<T>(
  items: readonly T[],
  getInstant: (item: T) => VpInstant,
  now: VpInstant,
  zone?: string
): VpDayGroup<T>[] {
  const z = zone ?? "local";
  const groups = new Map<number, VpDayGroup<T>>();

  for (const item of items) {
    const day = toDateTime(getInstant(item), z).startOf("day");
    if (!day.isValid) {
      continue;
    }
    const key = day.toMillis();
    let group = groups.get(key);
    if (!group) {
      const dayStart = day.toJSDate();
      group = { dayStart, label: dayLabel(dayStart, now, z), items: [] };
      groups.set(key, group);
    }
    group.items.push(item);
  }

  return [...groups.values()].sort((a, b) => b.dayStart.getTime() - a.dayStart.getTime());
}
